import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlarmClock, CalendarCheck, Handshake, Plus, UserRound } from 'lucide-react';
import { api } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { CustomerStatusBadge } from '../../components/customers/CustomerStatusBadge';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useUrlState } from '../../hooks/useUrlState';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { unwrapArray } from '../../lib/api-envelope';
import { formatCalendarDate, formatInstant } from '../../lib/calendar-date';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { salesActivityKindLabel } from '../../lib/sales-activity';
import type { SalesActivity, SalesActivityListParams } from '../../types/sales-activity';
import { useAuth } from '../../contexts/AuthContext';
import { isSalesRole } from '../../lib/sales-access';
import { MyFollowUpsList } from '../../components/sales/MyFollowUps';
import ScoutButton from '../../components/sales/ScoutButton';
import { ScoutBriefList } from '../../components/sales/ScoutBriefList';

/**
 * /sales/meetings — the meeting register (CRM-FE-2).
 *
 * EVERY CHIP IS ITS OWN API QUESTION, and its count is the length of that
 * answer: All (no filter), Mine (`mine=1`), Follow-up due (`follow_up=due`)
 * and Overdue (`follow_up=overdue`). Due and overdue are decided by the API on
 * the TENANT's today; this screen does no date maths. The list is capped at
 * 500 rows and has no counts endpoint, so a full page reads "500+" rather than
 * claiming an exact total it does not have.
 *
 * Confirmed meetings only: v1 never asks for Scout drafts (`drafts=1`), and
 * there is no "To confirm" chip until Scout can produce one.
 *
 * THREE STATES, NOT TWO: an empty answer is "No meetings logged yet"; a failed
 * or refused one is said so, in that same place.
 */
type ChipKey = 'all' | 'mine' | 'due' | 'overdue';
const CHIPS: { key: ChipKey; label: string; params?: SalesActivityListParams }[] = [
  { key: 'all', label: 'All' },
  { key: 'mine', label: 'Mine', params: { mine: 1 } },
  { key: 'due', label: 'Follow-up due', params: { follow_up: 'due' } },
  { key: 'overdue', label: 'Overdue', params: { follow_up: 'overdue' } },
];
const EMPTY_TEXT: Record<ChipKey, string> = {
  all: 'No meetings logged yet',
  mine: 'You have not logged a meeting yet',
  due: 'No follow-ups due',
  overdue: 'No overdue follow-ups',
};
const API_CAP = 500;

const countOf = (rows: SalesActivity[] | undefined): string | undefined =>
  rows === undefined ? undefined : rows.length >= API_CAP ? `${API_CAP}+` : String(rows.length);

function useMeetings(params?: SalesActivityListParams) {
  return useQuery({
    queryKey: ['sales-activities', 'list', params ?? {}],
    queryFn: () => api.salesActivities.list(params).then((r) => unwrapArray<SalesActivity>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export default function MeetingsScreen() {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  // A sales user has no Overview (and so no My Follow-ups widget); the same
  // list sits here for them (operator, 2026-10-14).
  const { user } = useAuth();
  const showFollowUps = isSalesRole(user?.role);
  const [chipParam, setChip] = useUrlState<string>('chip', 'all');
  const chip: ChipKey = CHIPS.some((c) => c.key === chipParam) ? (chipParam as ChipKey) : 'all';

  const queries: Record<ChipKey, ReturnType<typeof useMeetings>> = {
    all: useMeetings(),
    mine: useMeetings({ mine: 1 }),
    due: useMeetings({ follow_up: 'due' }),
    overdue: useMeetings({ follow_up: 'overdue' }),
  };
  // A failed read holds no data; `isLoading` is false while a failed read
  // waits to retry, so "no data and no failure" is what loading means here.
  const dataOf = (k: ChipKey) => (queryLoadFailure(queries[k]) ? undefined : queries[k].data);
  const loadingOf = (k: ChipKey) => !queryLoadFailure(queries[k]) && queries[k].data === undefined;

  const active = queries[chip];
  const failure = queryLoadFailure(active);
  const rows = dataOf(chip);
  const dueIds = new Set((dataOf('due') ?? []).map((r) => r.id));
  const overdueIds = new Set((dataOf('overdue') ?? []).map((r) => r.id));

  const open = (id: string) => navigate(`/sales/meetings/${id}`);
  const rowKeys = (id: string) => (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(id); }
  };

  const followUpCell = (r: SalesActivity) => {
    if (!r.followUpDate) return <span className="text-fieldiq-muted">—</span>;
    const day = formatCalendarDate(r.followUpDate) ?? r.followUpDate;
    // The flag comes from WHICH API answer the row is in, not from comparing
    // the date with a clock here.
    const flag = r.followUpDoneAt ? 'Done' : overdueIds.has(r.id) ? 'Overdue' : dueIds.has(r.id) ? 'Due' : null;
    return (
      <span className="flex flex-col">
        <span className="text-fieldiq-text">{day}</span>
        {flag && <span className="text-xs font-bold uppercase tracking-wide text-fieldiq-muted">{flag}</span>}
      </span>
    );
  };

  return (
    <div data-testid="meetings-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Sales"
        title="Meetings"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <>
            {/* Scout first: one spoken sentence logs the meeting. The form stays
                beside it as the band's secondary — no colour class, it inherits
                the band's near-white. */}
            <ScoutButton />
            <button type="button" onClick={() => navigate('/sales/meetings/new')} className="fi-action fi-action--standard border border-white/25 hover:border-white/60">
              <Plus className="size-4" aria-hidden="true" />
              Log meeting
            </button>
          </>
        }
        /* FOUR stats, and copy no longer than Rate Sheets': the band's circle
           clearance was only ever measured on a four-column strip, and a
           three-column strip moves column 3 under the ring. The long label sits
           in column 2; column 3 stays short (the ~70px ceiling). */
        stats={[
          { icon: <Handshake className="size-4" aria-hidden="true" />, label: 'Logged', sub: 'Confirmed meetings', tone: 'neutral', loading: loadingOf('all'), value: countOf(dataOf('all')) ?? '—' },
          { icon: <CalendarCheck className="size-4" aria-hidden="true" />, label: 'Follow-ups due', sub: 'Today or earlier', tone: 'info', loading: loadingOf('due'), value: countOf(dataOf('due')) ?? '—' },
          { icon: <UserRound className="size-4" aria-hidden="true" />, label: 'Mine', sub: 'Logged by you', tone: 'success', loading: loadingOf('mine'), value: countOf(dataOf('mine')) ?? '—' },
          { icon: <AlarmClock className="size-4" aria-hidden="true" />, label: 'Overdue', sub: 'Past the date', tone: 'accent', loading: loadingOf('overdue'), value: countOf(dataOf('overdue')) ?? '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* Scout: what to chase today, the caller's own (GET /scout/brief).
              Every CRM role — the page is CRM-only and the brief is each
              reader's own. */}
          <section aria-labelledby="scout-list-title" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card p-4" data-testid="scout-panel">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted">What to chase today</p>
            <h2 id="scout-list-title" className="mb-1 text-base font-extrabold tracking-tight text-white">Scout</h2>
            <ScoutBriefList />
          </section>
          {showFollowUps && (
            <section aria-labelledby="my-follow-ups-title" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
              <h2 id="my-follow-ups-title" className="mb-3 text-base font-extrabold tracking-tight text-white">My follow-ups</h2>
              <MyFollowUpsList />
            </section>
          )}
          <div className="scrollbar-hide flex gap-2 overflow-x-auto">
            {CHIPS.map((c) => {
              const isActive = chip === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setChip(c.key)}
                  className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    isActive ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {c.label}
                  {!loadingOf(c.key) && (
                    <span className={`tabular-nums ${isActive ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                      {countOf(dataOf(c.key)) ?? '—'}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <section aria-labelledby="meetings-register-title" className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
            <header className="flex items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
              <div>
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Meeting register</p>
                <h2 id="meetings-register-title" className="mt-1 text-base font-extrabold tracking-tight text-white">
                  {CHIPS.find((c) => c.key === chip)!.label === 'All' ? 'All meetings' : CHIPS.find((c) => c.key === chip)!.label}
                </h2>
              </div>
              {rows && rows.length >= API_CAP && (
                <p className="text-sm text-fieldiq-muted">Showing the newest {API_CAP}</p>
              )}
            </header>

            {failure ? (
              <LoadFailureNotice error={queryFailureError(active)} subject="meetings" />
            ) : rows === undefined ? (
              <div className="space-y-3 p-4" aria-busy="true">
                {[1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-fi-panel bg-fieldiq-dark/60" />)}
              </div>
            ) : rows.length === 0 ? (
              <p className="px-4 py-16 text-center text-sm text-fieldiq-muted">{EMPTY_TEXT[chip]}</p>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1000px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                      <tr>
                        {['Date', 'Customer', 'Contact', 'Kind', 'Next step', 'Follow-up', 'Owner'].map((h) => (
                          <th key={h} className="whitespace-nowrap px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-fieldiq-muted">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr
                          key={r.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => open(r.id)}
                          onKeyDown={rowKeys(r.id)}
                          className="cursor-pointer border-t border-fieldiq-border text-sm outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10"
                        >
                          <td className="whitespace-nowrap px-4 py-3 text-fieldiq-text">{formatInstant(r.occurredAt, timeZone) ?? '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="font-extrabold text-white">{r.customerName ?? '—'}</span>
                              <CustomerStatusBadge status={r.customerStatus} />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-fieldiq-text">{r.contactName ?? '—'}</td>
                          <td className="px-4 py-3 text-fieldiq-text">{salesActivityKindLabel(r.kind)}</td>
                          <td className="max-w-[18rem] truncate px-4 py-3 text-fieldiq-text">{r.nextStep ?? '—'}</td>
                          <td className="px-4 py-3">{followUpCell(r)}</td>
                          <td className="px-4 py-3 text-fieldiq-text">{r.ownerName ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {rows.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => open(r.id)}
                      className="block w-full rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4 text-left"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="min-w-0 truncate text-sm font-extrabold text-white">{r.customerName ?? '—'}</span>
                        <CustomerStatusBadge status={r.customerStatus} />
                      </div>
                      <p className="mt-1 text-sm text-fieldiq-muted">
                        {formatInstant(r.occurredAt, timeZone) ?? '—'} · {salesActivityKindLabel(r.kind)} · {r.ownerName ?? '—'}
                      </p>
                      {r.nextStep && <p className="mt-2 text-sm text-fieldiq-text">{r.nextStep}</p>}
                      <div className="mt-2 text-sm">Follow-up: {followUpCell(r)}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </section>
    </div>
  );
}
